import React from 'react';
import PropTypes from 'prop-types';
import { CalendarIcon, MapPinIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import MultilineText from '../components/MultilineText';
import { descriptionClassName } from '../lib/descriptionClassName';
import { descriptionWhiteSpace } from '../lib/descriptionWhiteSpace';
import clsx from 'clsx';
import Badge from '../components/Badge';
import ModulesEnum, { getEventModules } from '../enums/modules';
import sanitizeHtml from 'sanitize-html';
import { stripQueryStringFromCdnUrl } from '../services/getUrlFromCdn';
import useCheckoutState, { checkoutLayouts } from '../hooks/useCheckoutState';
import useAiTarget from '../hooks/useAiTarget';
import { getMedia } from '../lib/getMedia';
import { formatEventDate } from '../lib/format-date';
import { useCheckoutLocalization } from '@repo/checkout/hooks/useCheckoutLocalization';
import StyledElement from './StyledElement';
import TestModeBanner from './TestModeBanner';
import { checkoutTypes } from '@repo/checkout/enums/checkout-types';
import { Logo } from './Product/Product';
import getThemeLabels from '@repo/checkout/lib/getThemeLabels';
import Media from './Media';

function getTitle({ allowDynamicTitle, dynamicTitle, title }) {
  if (!allowDynamicTitle) return title;

  if (dynamicTitle) {
    return sanitizeHtml(dynamicTitle.replace(/\\n/g, '\n'));
  }

  return title;
}

function getDescription({
  allowDynamicDescription,
  dynamicDescription,
  productDescription,
  productDescriptionHtml,
  checkoutDescription,
  checkoutDescriptionHtml,
}) {
  if (dynamicDescription && allowDynamicDescription) {
    return sanitizeHtml(dynamicDescription.replace(/\\n/g, '\n'));
  }

  if (typeof productDescriptionHtml === 'string') {
    return productDescriptionHtml;
  } else if (typeof productDescription === 'string') {
    return productDescription;
  } else if (typeof checkoutDescriptionHtml === 'string') {
    return checkoutDescriptionHtml;
  } else if (typeof checkoutDescription === 'string') {
    return checkoutDescription;
  }

  return '';
}

const propTypes = {
  checkout: PropTypes.object.isRequired,
  seller: PropTypes.object.isRequired,
};

const EventDetails = ({ flushMediaBottom = false }) => {
  const { theme, isScreenshot, checkoutLayout, checkout, seller, query, isPreview, checkoutType } =
    useCheckoutState();
  const aiTarget = useAiTarget();
  const { locale } = useCheckoutLocalization();

  const modules = getEventModules({
    theme,
    checkout,
    seller,
    isScreenshot,
    query,
  });

  const siteUrl = seller.url;

  const ImageWrapper = siteUrl ? 'a' : 'div';

  const isSoldOut = checkout.eventDetails?.isSoldOut;
  const isCanceled = checkout.eventDetails?.status === 'canceled';

  const title = getTitle({
    allowDynamicTitle: checkout.allowDynamicTitle,
    title: checkout.title,
    dynamicTitle: query.title,
  });

  const description = (() => {
    const description = getDescription({
      allowDynamicDescription: checkout.allowDynamicDescription,
      dynamicDescription: query.description,
      checkoutDescription: checkout.description,
      checkoutDescriptionHtml: checkout.descriptionHtml,
    });

    if (isPreview) {
      return stripQueryStringFromCdnUrl(description);
    }

    return description;
  })();

  const labels = getThemeLabels(theme);

  const media = getMedia({ checkout, product: checkout.product });
  const hasMedia = media && media.length > 0;

  // Layout type checks
  const isEmbed = checkoutType === checkoutTypes.EMBED;
  const isPopup = checkoutType === checkoutTypes.POPUP;
  const isCheckout = checkoutType === checkoutTypes.CHECKOUT;

  const hasLogoBubble =
    modules[ModulesEnum.LOGO] &&
    (isEmbed ||
      isPopup ||
      (isCheckout && theme.elements?.product?.logo?.hostedPageLogoStyle === 'logoBubble'));

  const logoSizePx = Number(theme?.elements?.product?.logo?.logoBubble?.styles?.size) || 80;

  const isDoubleLayout = checkoutLayout === checkoutLayouts.DOUBLE;
  const isSplitLayout = checkoutLayout === checkoutLayouts.SPLIT;
  const isCenterLayout = !isDoubleLayout && !isSplitLayout;

  // Logo positioning: use relative positioning when there's no media to position against
  const shouldUseRelativePositioning =
    (isEmbed && !hasMedia) || (isCheckout && (isDoubleLayout || isSplitLayout) && !hasMedia);

  const hasEventWrapperContent =
    hasLogoBubble ||
    modules[ModulesEnum.TITLE] ||
    modules[ModulesEnum.PRICE] ||
    modules[ModulesEnum.DATE] ||
    modules[ModulesEnum.LOCATION] ||
    modules[ModulesEnum.MEETING_LINK] ||
    modules[ModulesEnum.DESCRIPTION];

  return (
    <div>
      {hasMedia && (
        <div
          {...aiTarget('images')}
          className={clsx('cp_product-image-wrapper', {
            'mb-4':
              (isDoubleLayout || isSplitLayout || isCenterLayout) && !hasLogoBubble && !hasMedia,
          })}
          style={
            isCenterLayout
              ? {
                  marginTop: 'calc(-1 * var(--cp_checkout-padding, 0px))',
                  marginLeft: 'calc(-1rem - var(--cp_checkout-padding, 0px))',
                  marginRight: 'calc(-1rem - var(--cp_checkout-padding, 0px))',
                  // When media is the only thing in the content section, extend
                  // the image down to the divider by cancelling the parent's pb-4.
                  // Only flush when nothing (ticket types/variants) sits between
                  // the image and the divider — caller decides via flushMediaBottom.
                  ...(flushMediaBottom && !hasEventWrapperContent && { marginBottom: '-1rem' }),
                }
              : undefined
          }
        >
          <Media media={media} />
        </div>
      )}

      {hasEventWrapperContent && (
        <StyledElement
          elementPath="product"
          className={clsx(
            'cp_product-wrapper',
            '[text-align:var(--cp_product-text-align)]',
            'text-[var(--cp_product-color)]',
            'flex flex-col w-full relative',
            {
              // Double layout with media needs spacing between product wrapper and media
              'mt-4': !hasLogoBubble && hasMedia,
            },
          )}
          /*style={{
          // Checkout without media needs top margin to account for absolute positioned logo
          ...(hasLogoBubble &&
            isCheckout &&
            isCenterLayout &&
            !hasMedia && {
              marginTop: `calc(${logoSizePx / 4}px - var(--cp_checkout-padding))`,
            }),
        }}*/
        >
          {hasLogoBubble && (
            <>
              <div
                className={clsx(
                  'cp_product-logo-wrapper',
                  'leading-0',
                  '[text-align:var(--cp_product-logo-bubble-text-align)]',
                  {
                    // Absolute positioning centers logo at top of product section
                    'absolute left-0 right-0': !shouldUseRelativePositioning,
                    // Embed with relative positioning needs margin adjustment
                    'mt-4': shouldUseRelativePositioning && isEmbed,
                  },
                )}
                style={{
                  // Position logo half its height above the product section top
                  ...(!shouldUseRelativePositioning &&
                    hasMedia && {
                      top: `calc(-1 * (${logoSizePx / 2}px))`,
                    }),
                  ...(!shouldUseRelativePositioning &&
                    !hasMedia && {
                      top: `calc(-1 * (${logoSizePx / 2}px + var(--cp_checkout-padding)))`,
                    }),
                }}
              >
                <ImageWrapper
                  className={clsx(
                    'cp_product-logo',
                    'inline-block rounded-full overflow-hidden shadow-sm text-5xl',
                    'bg-[var(--cp_product-logo-bubble-background-color)]',
                    {
                      'hover:shadow': !!siteUrl,
                    },
                  )}
                  rel="nofollow noreferrer"
                  href={siteUrl}
                  target="_blank"
                  style={{
                    width: `${logoSizePx}px`,
                    height: `${logoSizePx}px`,
                  }}
                >
                  <Logo width={logoSizePx} height={logoSizePx} isLogoBubble />
                </ImageWrapper>
              </div>

              {/* spacer div to avoid overlap, 50% of logo size */}
              {!shouldUseRelativePositioning && (
                <div
                  style={{
                    marginBottom: hasMedia
                      ? `max(0px, calc(${logoSizePx / 2}px))`
                      : `max(0px, calc(${logoSizePx / 2}px - var(--cp_checkout-padding))`,
                  }}
                />
              )}
            </>
          )}

          {(modules[ModulesEnum.TITLE] || modules[ModulesEnum.PRICE]) && (
            <div
              className={clsx('cp_product-content mb-2', {
                'mt-4': isCenterLayout && !hasLogoBubble && !hasMedia,
              })}
            >
              <TestModeBanner placement="title" />

              <div className="cp_product-title-wrapper justify-between items-start">
                {modules[ModulesEnum.TITLE] && (
                  <div className="cp_product-title-container" {...aiTarget('title')}>
                    <StyledElement
                      elementPath="product.title"
                      as="h1"
                      className={clsx(
                        'cp_product-title',
                        'break-words',
                        'bg-[var(--cp_product-title-background-color)]',
                        'text-[var(--cp_product-title-color)]',
                        '[font-family:var(--cp_product-title-font-family)]',
                        '[font-style:var(--cp_product-title-font-style)]',
                        '[font-size:var(--cp_product-title-font-size)]',
                        '[line-height:var(--cp_product-title-line-height)]',
                        '[font-weight:var(--cp_product-title-font-weight)]',
                      )}
                      checkEnabled={true}
                    >
                      {title}
                    </StyledElement>
                  </div>
                )}
              </div>

              {isCanceled ? (
                <Badge
                  className={clsx(
                    'cp_canceled',
                    'text-[var(--cp_checkout-statusBadges-canceled-color)]',
                    'bg-[var(--cp_checkout-statusBadges-canceled-background-color)]',
                    '[font-family:var(--cp_checkout-statusBadges-font-family)]',
                    '[font-style:var(--cp_checkout-statusBadges-font-style)]',
                    '[font-size:var(--cp_checkout-statusBadges-font-size)]',
                    '[line-height:var(--cp_checkout-statusBadges-line-height)]',
                    '[font-weight:var(--cp_checkout-statusBadges-font-weight)]',
                  )}
                >
                  {labels.canceledText}
                </Badge>
              ) : (
                isSoldOut && (
                  <Badge
                    className={clsx(
                      'cp_sold-out',
                      'text-[var(--cp_checkout-statusBadges-soldOut-color)]',
                      'bg-[var(--cp_checkout-statusBadges-soldOut-background-color)]',
                      '[font-family:var(--cp_checkout-statusBadges-font-family)]',
                      '[font-style:var(--cp_checkout-statusBadges-font-style)]',
                      '[font-size:var(--cp_checkout-statusBadges-font-size)]',
                      '[line-height:var(--cp_checkout-statusBadges-line-height)]',
                      '[font-weight:var(--cp_checkout-statusBadges-font-weight)]',
                    )}
                  >
                    {labels.soldOutText}
                  </Badge>
                )
              )}
            </div>
          )}

          {(modules[ModulesEnum.DATE] ||
            modules[ModulesEnum.LOCATION] ||
            modules[ModulesEnum.MEETING_LINK]) && (
            <div
              {...aiTarget('eventDetails')}
              className={clsx('cp_event-details-wrapper flex flex-col space-y-1', {
                'mb-4': modules[ModulesEnum.DESCRIPTION],
                'mt-4':
                  isCenterLayout &&
                  !hasLogoBubble &&
                  !hasMedia &&
                  !modules[ModulesEnum.TITLE] &&
                  !modules[ModulesEnum.PRICE],
              })}
            >
              {modules[ModulesEnum.DATE] && (
                <StyledElement
                  elementPath="product.event.date"
                  as="div"
                  style={{
                    fontWeight: 'calc(var(--cp_font-body-weight) + 100)',
                  }}
                  className={clsx(
                    'cp_event-details-date-wrapper flex gap-x-2',
                    'text-[var(--cp_product-event-date-color)]',
                    '[font-family:var(--cp_font-body)]',
                    '[font-style:var(--cp_font-body-style)]',
                    '[font-size:var(--cp_font-body-size)]',
                    '[line-height:var(--cp_font-body-line-height)]',
                    {
                      'justify-start': theme.elements?.product?.styles?.textAlign === 'left',
                      'justify-center': theme.elements?.product?.styles?.textAlign === 'center',
                      'justify-end': theme.elements?.product?.styles?.textAlign === 'right',
                    },
                  )}
                  checkEnabled={true}
                >
                  <div className="cp_event-details-date-icon-wrapper">
                    <CalendarIcon className="cp_event-details-date-icon h-5 w-5" />
                  </div>
                  <div className="cp_event-details-date flex flex-col">
                    {formatEventDate({
                      startDate: checkout.eventDetails.startDate,
                      endDate: checkout.eventDetails.endDate,
                      timezone: checkout.eventDetails.timezone,
                      locale,
                    }).map(date => (
                      <span key={date}>{date}</span>
                    ))}
                  </div>
                </StyledElement>
              )}

              {modules[ModulesEnum.LOCATION] &&
                (checkout.eventDetails.type === 'in_person' ||
                  checkout.eventDetails.type === 'hybrid') && (
                  <StyledElement
                    elementPath="product.event.location"
                    as="div"
                    style={{
                      fontWeight: 'calc(var(--cp_font-body-weight) + 100)',
                    }}
                    className={clsx(
                      'cp_event-details-location-wrapper',
                      'flex items-center gap-x-2',
                      'text-[var(--cp_product-event-location-color)]',
                      '[font-family:var(--cp_font-body)]',
                      '[font-style:var(--cp_font-body-style)]',
                      '[font-size:var(--cp_font-body-size)]',
                      '[line-height:var(--cp_font-body-line-height)]',
                      {
                        'justify-start': theme.elements?.product?.styles?.textAlign === 'left',
                        'justify-center': theme.elements?.product?.styles?.textAlign === 'center',
                        'justify-end': theme.elements?.product?.styles?.textAlign === 'right',
                      },
                    )}
                    checkEnabled={true}
                  >
                    <div className="cp_event-details-location-icon-wrapper">
                      <MapPinIcon className="cp_event-details-location-icon h-5 w-5" />
                    </div>
                    {checkout.eventDetails.mapsLink && (
                      <a
                        href={checkout.eventDetails.mapsLink}
                        target="_blank"
                        rel="nofollow noreferrer"
                        className="cp_event-details-location-link underline"
                      >
                        <span className="cp_event-details-location">
                          {checkout.eventDetails.location}
                        </span>
                      </a>
                    )}

                    {!checkout.eventDetails.mapsLink && (
                      <span className="cp_event-details-location">
                        {checkout.eventDetails.location}
                      </span>
                    )}
                  </StyledElement>
                )}

              {modules[ModulesEnum.MEETING_LINK] &&
                (checkout.eventDetails.type === 'virtual' ||
                  checkout.eventDetails.type === 'hybrid') && (
                  <StyledElement
                    elementPath="product.event.virtual"
                    as="div"
                    style={{
                      fontWeight: 'calc(var(--cp_font-body-weight) + 100)',
                    }}
                    className={clsx(
                      'cp_event-details-virtual-wrapper',
                      'flex items-center gap-x-2',
                      'text-[var(--cp_product-event-virtual-color)]',
                      '[font-family:var(--cp_font-body)]',
                      '[font-style:var(--cp_font-body-style)]',
                      '[font-size:var(--cp_font-body-size)]',
                      '[line-height:var(--cp_font-body-line-height)]',
                      {
                        'justify-start': theme.elements?.product?.styles?.textAlign === 'left',
                        'justify-center': theme.elements?.product?.styles?.textAlign === 'center',
                        'justify-end': theme.elements?.product?.styles?.textAlign === 'right',
                      },
                    )}
                    checkEnabled={true}
                  >
                    <div className="cp_event-details-virtual-icon-wrapper">
                      <VideoCameraIcon className="cp_event-details-virtual-icon h-5 w-5" />
                    </div>
                    <span className="cp_event-details-virtual">
                      {checkout.eventDetails.type === 'hybrid'
                        ? labels.hybridEventText
                        : labels.virtualEventText}
                    </span>
                  </StyledElement>
                )}
            </div>
          )}

          {modules[ModulesEnum.DESCRIPTION] && (
            <StyledElement
              elementPath="description"
              {...aiTarget('description')}
              className={clsx(
                'cp_product-description-wrapper',
                'flow-root overflow-hidden supports-[overflow:clip]:overflow-x-clip supports-[overflow:clip]:overflow-y-visible',
              )}
              style={{
                ...(isCenterLayout &&
                  !modules[ModulesEnum.MEDIA] &&
                  !modules[ModulesEnum.TITLE] &&
                  !modules[ModulesEnum.PRICE] &&
                  !modules[ModulesEnum.DATE] &&
                  !modules[ModulesEnum.LOCATION] &&
                  !modules[ModulesEnum.MEETING_LINK] && {
                    marginTop: `max(0px, calc(16px - var(--cp_checkout-padding)))`,
                  }),
              }}
            >
              <div className={descriptionClassName(theme?.elements?.product?.styles?.textAlign)}>
                <MultilineText
                  whiteSpace={descriptionWhiteSpace(description)}
                  dangerouslySetInnerHTML={{
                    __html: description,
                  }}
                />
              </div>
            </StyledElement>
          )}
        </StyledElement>
      )}
    </div>
  );
};

EventDetails.propTypes = propTypes;

export default EventDetails;
